/**
 * Fee Assumptions Library - React Query Hooks
 * Phase 7 Batch 7.3.3 - Fee Assumptions Library
 *
 * Hooks for managing tenant-scoped fee assumption library.
 * Used in Settings page and project forms for quick-add functionality.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { toast } from 'sonner';

export interface FeeAssumption {
  id: string;
  assumption_text: string;
  display_order: number;
  created_at: string;
  updated_at: string;
}

/**
 * Query: Get all fee assumptions for current tenant
 */
export function useLibraryAssumptions() {
  return useQuery({
    queryKey: ['fee-assumptions'],
    queryFn: () => api.feeAssumptions.list(),
  });
}

/**
 * Mutation: Create new fee assumption
 */
export function useCreateAssumption() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (assumptionText: string) =>
      api.feeAssumptions.create({ assumption_text: assumptionText }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fee-assumptions'] });
      toast.success('Fee assumption added');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to add fee assumption');
    },
  });
}

/**
 * Mutation: Update fee assumption text
 */
export function useUpdateAssumption() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, assumptionText }: { id: string; assumptionText: string }) =>
      api.feeAssumptions.update(id, { assumption_text: assumptionText }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fee-assumptions'] });
      toast.success('Fee assumption updated');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update fee assumption');
    },
  });
}

/**
 * Mutation: Delete fee assumption
 */
export function useDeleteAssumption() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.feeAssumptions.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fee-assumptions'] });
      toast.success('Fee assumption deleted');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete fee assumption');
    },
  });
}
